import React, { useEffect } from 'react';
import MainLayout from '../utilities/layouts/MainLayout';
import { useHistory } from 'react-router-dom';
import { CardItem, CardList, EmptyItem, MobileHomeButton } from '../utilities/commonUtilities/CardItem';
import { ReactComponent as LabBadge } from '../../resources/images/lab-badge.svg';
import { ReactComponent as LabEmpty } from '../../resources/images/lab-empty.svg';
import { Typography, makeStyles } from '@material-ui/core';
import { PageTitleBlock } from '../utilities/commonUtilities/GenericDivElements';
import { connect } from 'react-redux';
import { PATH_LABS_PSA, PATH_LABS_TESTOSTERONE, PATH_LABS_HCG, PATH_LABS_AFP, PATH_LABS_LDH, PATH_LABS_FSH, PATH_LABS_LH } from '../../containers/RoutePaths';
import { actionObservation } from './ObservationPage';
import { observationCodes } from './ObservationPage';
import { RequiresLogin } from '../utilities/commonUtilities/RequiresLogin';
import { COLOR_WHITE } from '../utilities/MaterialWatchman';
import { styleBreakMobile } from '../utilities/layouts/Globals';
import clsx from 'clsx';

const isWatchman = process.env.REACT_APP_THEME_ENV === 'watchman';

const useStyles = makeStyles(theme => ({
    emptyLab: {
        background: COLOR_WHITE,
    },
    cardList: {
        padding: theme.spacing(0.25),
        marginBottom: theme.spacing(3),
        '& div.MuiGrid-item': {
            marginTop: theme.spacing(0),
        },
    },
    cardItem: {
        marginBottom: theme.spacing(1.5),
    },
    largeText: {
        fontSize: theme.spacing(5),
        lineHeight: '55px',
        fontWeight: 700,
        fontStyle: 'normal',
        [styleBreakMobile(theme)]: {
            fontSize: theme.spacing(4),
            textAlign: 'center',
            lineHeight: '41px',
        },
    },
    subtitleText: {
        letterSpacing: '0.3px',
    },
    subtitleLabText: {
        color: theme.palette.secondary.contrastText,
    },
}));

export const filterObservationResults = (observationResults, observationResultCodeArray) => {
    return observationResults.filter(result => observationResultCodeArray?.includes(result.code));
};

const emptyLabMessage = 'Lab results completed for your virtual visit will show up here.';

const EmptyLab = () => {
    const classes = useStyles();
    const SubtitleText = () => {
        return (
            <>
                <Typography className={clsx(classes.largeText, classes.subtitleText)}>
                    Sorry,
                    <br />
                    No{' '}
                    <Typography className={clsx(classes.largeText, classes.subtitleLabText)} component="span">
                        Lab
                    </Typography>{' '}
                    Results.
                </Typography>
            </>
        );
    };

    return (
        <div className={classes.emptyLab}>
            <EmptyItem
                image={LabEmpty}
                title={SubtitleText}
                message={emptyLabMessage}
                isFullscreen={isWatchman}
                showShadow={isWatchman}
                hideNavbar={isWatchman}
            />
        </div>
    );
};

function LabsContent({ observationResults, patientFhirId, fetchObservation }) {
    const history = useHistory();
    const classes = useStyles();
    const noDataText = 'No historial data';

    useEffect(() => {
        observationResults.length === 0 && fetchObservation(patientFhirId);
    }, [fetchObservation, patientFhirId, observationResults.length]);

    const psaResults = filterObservationResults(observationResults, observationCodes.PSA.code);
    const testosteroneResults = filterObservationResults(observationResults, observationCodes.Testosterone.code);
    const hcgResults = filterObservationResults(observationResults, observationCodes.HCG.code);
    const afpResults = filterObservationResults(observationResults, observationCodes.AFP.code);
    const fshResults = filterObservationResults(observationResults, observationCodes.FSH.code);
    const ldhResults = filterObservationResults(observationResults, observationCodes.LDH.code);
    const lhResults = filterObservationResults(observationResults, observationCodes.LH.code);

    return observationResults.length > 0 ? (
        <CardList className={classes.cardList}>
            {psaResults.length > 0 && (
                <CardItem
                    Badge={LabBadge}
                    title={observationCodes.PSA.name}
                    onClick={() => history.push(PATH_LABS_PSA)}
                    subtitle={`Latest Result: ${psaResults[0].date}`}
                    className={classes.cardItem}
                />
            )}
            {testosteroneResults.length > 0 && (
                <CardItem
                    Badge={LabBadge}
                    title={observationCodes.Testosterone.name}
                    onClick={() => history.push(PATH_LABS_TESTOSTERONE)}
                    subtitle={`Latest Result: ${testosteroneResults.length > 0 ? testosteroneResults[0].date : noDataText}`}
                    className={classes.cardItem}
                />
            )}
            {isWatchman && hcgResults.length > 0 && (
                <CardItem
                    Badge={LabBadge}
                    title={observationCodes.HCG.name}
                    onClick={() => history.push(PATH_LABS_HCG)}
                    subtitle={`Latest Result: ${hcgResults.length > 0 ? hcgResults[0].date : noDataText}`}
                    className={classes.cardItem}
                />
            )}
            {isWatchman && afpResults.length > 0 && (
                <CardItem
                    Badge={LabBadge}
                    title={observationCodes.AFP.name}
                    onClick={() => history.push(PATH_LABS_AFP)}
                    subtitle={`Latest Result: ${afpResults.length > 0 ? afpResults[0].date : noDataText}`}
                    className={classes.cardItem}
                />
            )}
            {isWatchman && fshResults.length > 0 && (
                <CardItem
                    Badge={LabBadge}
                    title={observationCodes.FSH.name}
                    onClick={() => history.push(PATH_LABS_FSH)}
                    subtitle={`Latest Result: ${fshResults.length > 0 ? fshResults[0].date : noDataText}`}
                    className={classes.cardItem}
                />
            )}
            {isWatchman && ldhResults.length > 0 && (
                <CardItem
                    Badge={LabBadge}
                    title={observationCodes.LDH.name}
                    onClick={() => history.push(PATH_LABS_LDH)}
                    subtitle={`Latest Result: ${ldhResults.length > 0 ? ldhResults[0].date : noDataText}`}
                    className={classes.cardItem}
                />
            )}
            {isWatchman && lhResults.length > 0 && (
                <CardItem
                    Badge={LabBadge}
                    title={observationCodes.LH.name}
                    onClick={() => history.push(PATH_LABS_LH)}
                    subtitle={`Latest Result: ${lhResults.length > 0 ? lhResults[0].date : noDataText}`}
                    className={classes.cardItem}
                />
            )}
        </CardList>
    ) : (
        !isWatchman && <EmptyItem image={LabEmpty} title="No Lab Results" message={emptyLabMessage} />
    );
}

function LabsPage({ observationResults, patientFhirId, fetchObservation }) {
    return (
        <RequiresLogin>
            <MainLayout noHiddenOverflowX={true}>
                {observationResults.length > 0 && (
                    <PageTitleBlock>
                        <Typography variant="h1">Labs</Typography>
                    </PageTitleBlock>
                )}
                <LabsContent observationResults={observationResults} patientFhirId={patientFhirId} fetchObservation={fetchObservation} />
            </MainLayout>
            {observationResults.length === 0 ? isWatchman ? <EmptyLab /> : <MobileHomeButton /> : null}
        </RequiresLogin>
    );
}

const mapStateToProps = state => {
    return {
        observationResults: state.observation.labResults,
        patientFhirId: state.login.session.patientId,
    };
};

const mapDispatchToProps = {
    fetchObservation: actionObservation,
};

export default connect(mapStateToProps, mapDispatchToProps)(LabsPage);
